/**
 * Module: `export/render-docx.ts` — ResponseExportDocument → DOCX Blob.
 * Typography mirrors the PDF export (PDF = reference).
 */

import { enhanceDocxHyperlinks } from "@/lib/export/docx-hyperlinks";
import {
  createTightBulletLevels,
  createTightOrderedLevels,
} from "@/lib/export/docx-list-levels";
import {
  EXPORT_BODY_FONT_SIZE_PT,
  EXPORT_COLOR_BODY,
  EXPORT_COLOR_H1,
  EXPORT_COLOR_H2,
  EXPORT_COLOR_H3,
  EXPORT_COLOR_LINK,
  EXPORT_H1_AFTER_PT,
  EXPORT_H1_FONT_SIZE_PT,
  EXPORT_H2_AFTER_PT,
  EXPORT_H2_BEFORE_PT,
  EXPORT_H2_FONT_SIZE_PT,
  EXPORT_H3_AFTER_PT,
  EXPORT_H3_BEFORE_PT,
  EXPORT_H3_FONT_SIZE_PT,
  EXPORT_LINE_HEIGHT,
  EXPORT_LIST_AFTER_PT,
  EXPORT_PAGE_MARGINS_TWIPS,
  EXPORT_PARAGRAPH_AFTER_PT,
  EXPORT_SANS_FONT_DOCX,
  lineHeightToDocxLine,
  ptToHalfPoints,
  ptToTwips,
} from "@/lib/export/export-styles";
import { toExportMarkdown } from "@/lib/export/export-markdown";
import type { ResponseExportDocument } from "@/lib/export/response-export-document";

export async function renderExportToDocx(
  doc: ResponseExportDocument
): Promise<Blob> {
  const { md2docx } = await import("@m2d/md2docx");
  const markdown = toExportMarkdown(doc);
  const line = lineHeightToDocxLine(EXPORT_LINE_HEIGHT);
  const result = await md2docx(
    markdown,
    {
      title: doc.title,
      creator: "SidonIAssist",
      description: "Export de réponse SidonIAssist",
      styles: {
        default: {
          document: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_BODY_FONT_SIZE_PT),
              color: EXPORT_COLOR_BODY,
            },
            paragraph: {
              spacing: {
                after: ptToTwips(EXPORT_PARAGRAPH_AFTER_PT),
                line,
                lineRule: "auto",
              },
            },
          },
          heading1: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_H1_FONT_SIZE_PT),
              bold: true,
              color: EXPORT_COLOR_H1,
            },
            paragraph: {
              spacing: {
                before: 0,
                after: ptToTwips(EXPORT_H1_AFTER_PT),
                line,
              },
            },
          },
          heading2: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_H2_FONT_SIZE_PT),
              bold: true,
              color: EXPORT_COLOR_H2,
            },
            paragraph: {
              spacing: {
                before: ptToTwips(EXPORT_H2_BEFORE_PT),
                after: ptToTwips(EXPORT_H2_AFTER_PT),
                line,
              },
            },
          },
          heading3: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_H3_FONT_SIZE_PT),
              bold: true,
              color: EXPORT_COLOR_H3,
            },
            paragraph: {
              spacing: {
                before: ptToTwips(EXPORT_H3_BEFORE_PT),
                after: ptToTwips(EXPORT_H3_AFTER_PT),
                line,
              },
            },
          },
          hyperlink: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_BODY_FONT_SIZE_PT),
              color: EXPORT_COLOR_LINK,
              underline: {
                type: "single",
                color: EXPORT_COLOR_LINK,
              },
            },
          },
          listParagraph: {
            run: {
              font: EXPORT_SANS_FONT_DOCX,
              size: ptToHalfPoints(EXPORT_BODY_FONT_SIZE_PT),
              color: EXPORT_COLOR_BODY,
            },
            paragraph: {
              spacing: {
                after: ptToTwips(EXPORT_LIST_AFTER_PT),
                line,
              },
            },
          },
        },
      },
    },
    {
      // Avoid Word "Title" style (often clips at the page top).
      useTitle: false,
      properties: {
        page: {
          margin: {
            top: EXPORT_PAGE_MARGINS_TWIPS.top,
            right: EXPORT_PAGE_MARGINS_TWIPS.right,
            bottom: EXPORT_PAGE_MARGINS_TWIPS.bottom,
            left: EXPORT_PAGE_MARGINS_TWIPS.left,
          },
        },
      },
    },
    "blob",
    {
      list: {
        levels: createTightOrderedLevels(),
        bulletLevels: createTightBulletLevels(),
      },
    }
  );

  if (!(result instanceof Blob)) {
    throw new Error("Échec de génération du document Word.");
  }

  return enhanceDocxHyperlinks(result);
}
